import {
    AUTH_ERROR_CODES,
    type AuthErrorCode,
    type AuthErrorInfo,
} from "./auth-error-codes";
import { getErrorMessage } from "./auth-error-messages";

/**
 * Generate a unique error reference ID for support tickets
 * Format: ERR-YYYYMMDD-XXXXX (e.g., ERR-20260127-A3F2K)
 */
export function generateErrorReferenceId(): string {
    const now = new Date();
    const datePart = now.toISOString().slice(0, 10).replace(/-/g, "");
    const randomPart = Math.random().toString(36).substring(2, 7).toUpperCase();
    return `ERR-${datePart}-${randomPart}`;
}

/**
 * Determine if an error is a network error
 */
export function isNetworkError(error: Error): boolean {
    return (
        (error.name === "TypeError" &&
            (error.message.includes("fetch") ||
                error.message.includes("network"))) ||
        error.message.includes("Failed to fetch") ||
        error.message.includes("NetworkError") ||
        error.message.includes("Network request failed")
    );
}

/**
 * Extract error code from various error types
 */
export function extractErrorCode(error: unknown): AuthErrorCode {
    if (error && typeof error === "object") {
        // AuthError from authenticatedFetch.ts or backend error response
        if ("code" in error && typeof error.code === "string") {
            // Check if it's a known error code
            const knownCodes = Object.values(AUTH_ERROR_CODES);
            if (knownCodes.includes(error.code as AuthErrorCode)) {
                return error.code as AuthErrorCode;
            }
        }
        // Check for error response structure from backend
        if (
            "error" in error &&
            error.error &&
            typeof error.error === "object" &&
            "code" in error.error
        ) {
            const knownCodes = Object.values(AUTH_ERROR_CODES);
            if (knownCodes.includes(error.error.code as AuthErrorCode)) {
                return error.error.code as AuthErrorCode;
            }
        }
    }

    if (error instanceof Error) {
        if (isNetworkError(error)) {
            return AUTH_ERROR_CODES.NETWORK_ERROR;
        }
        // Check for common error message patterns
        if (error.message.includes("Token exchange failed")) {
            return AUTH_ERROR_CODES.EXCHANGE_FAILED;
        }
        if (
            error.message.includes("session expired") ||
            error.message.includes("Session expired")
        ) {
            return AUTH_ERROR_CODES.SESSION_EXPIRED;
        }
        if (error.message.includes("refresh")) {
            return AUTH_ERROR_CODES.TOKEN_REFRESH_FAILED;
        }
    }

    return AUTH_ERROR_CODES.UNKNOWN_ERROR;
}

/**
 * Create a structured AuthErrorInfo from various error types
 */
export function createAuthErrorInfo(error: unknown): AuthErrorInfo {
    const code = extractErrorCode(error);
    const messageConfig = getErrorMessage(code);

    let rawMessage = "An unknown error occurred";
    if (error instanceof Error) {
        rawMessage = error.message;
    } else if (typeof error === "string") {
        rawMessage = error;
    } else if (error && typeof error === "object" && "message" in error) {
        rawMessage = String(error.message);
    }

    return {
        code,
        message: rawMessage,
        referenceId: generateErrorReferenceId(),
        timestamp: new Date(),
        isRetryable: messageConfig.isRetryable,
    };
}

/**
 * Parse error from URL query params (for callback pages)
 */
export function parseUrlError(
    searchParams: URLSearchParams,
): AuthErrorInfo | null {
    const errorCode = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    if (!errorCode) return null;

    // Map common URL error codes
    let code: AuthErrorCode = AUTH_ERROR_CODES.UNKNOWN_ERROR;
    const knownCodes = Object.values(AUTH_ERROR_CODES);
    if (knownCodes.includes(errorCode as AuthErrorCode)) {
        code = errorCode as AuthErrorCode;
    } else if (errorCode === "exchange_failed") {
        code = AUTH_ERROR_CODES.EXCHANGE_FAILED;
    }

    const messageConfig = getErrorMessage(code);

    return {
        code,
        message: errorDescription || errorCode,
        referenceId: generateErrorReferenceId(),
        timestamp: new Date(),
        isRetryable: messageConfig.isRetryable,
    };
}
